import { useState, useRef, useEffect } from 'react';
import ArticleIcon from '@mui/icons-material/Article';
import { useSelector, useDispatch } from 'react-redux';
import { hcGetBotSummary, hcPollBotSummary, hcDownloadCreationsTemplate, hcDownloadChangesTemplate, hcDownloadAddActivityTemplate } from '../actions/helixCrqsThunks';
import {
  HelixUploadCrqsToCreateRequest,
  HelixUploadCrqsToChangeRequest,
  HelixUploadCrqsToAddActivityRequest,
  HelixUploadLockedUnlockedRequest,
} from '../requests/helixCrqsRequests';
import { Page, Header } from 'components';
import { Card, CardContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import './HelixCrqs.css';

const useStyles = makeStyles((theme) => ({
  root:      { padding: theme.spacing(3) },
  container: { marginTop: theme.spacing(3) },
}));

// ─── UPLOAD CONFIGS ───────────────────────────────────────────────────────────
const UPLOAD_CONFIGS = [
  {
    key:           'toCreate',
    title:         'Upload CRQ(s) to Create',
    badge:         { label: 'Create',   bg: '#eff6ff', color: '#1d4ed8' },
    request:       HelixUploadCrqsToCreateRequest,
    downloadThunk: hcDownloadCreationsTemplate,
    loadingKey:    'downloadCreationsTemplate',
  },
  {
    key:           'toChange',
    title:         'Upload CRQ(s) to Change',
    badge:         { label: 'Change',   bg: '#f0fdf4', color: '#16a34a' },
    request:       HelixUploadCrqsToChangeRequest,
    downloadThunk: hcDownloadChangesTemplate,
    loadingKey:    'downloadChangesTemplate',
  },
  {
    key:           'toAddActivity',
    title:         'Upload CRQ(s) to Add Activity',
    badge:         { label: 'Activity', bg: '#faf5ff', color: '#7c3aed' },
    request:       HelixUploadCrqsToAddActivityRequest,
    downloadThunk: hcDownloadAddActivityTemplate,
    loadingKey:    'downloadAddActivityTemplate',
    botOptions:    [1, 2, 3, 4, 5, 6],
    defaultBot:    6,
    lockedBot:     true,
  },
];

const UPLOAD_LOCK_CONFIGS = [
  {
    key:     'lockedUnlocked',
    title:   'Upload Locked/Unlocked',
    badge:   { label: 'Locked/Unlocked', bg: '#fffbeb', color: '#d97706' },
    request: HelixUploadLockedUnlockedRequest,
    noBot:   true,
    accept:  '.csv',
  },
];

// ─── UPLOAD CARD ──────────────────────────────────────────────────────────────
function UploadCard({ config, userId, dispatch, loading }) {
  const [file,    setFile]    = useState(null);
  const [bot,     setBot]     = useState(config.defaultBot ?? 1);
  const [status,  setStatus]  = useState(null); // null | 'uploading' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const inputRef              = useRef(null);

  const fileExt    = config.accept ?? '.xlsx';
  const MAX_SIZE_MB    = 50;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

  const validateFile = (f) => {
    if (!f.name.endsWith(fileExt)) return `Only ${fileExt} files are accepted.`;
    if (f.size > MAX_SIZE_BYTES)   return `File exceeds the ${MAX_SIZE_MB}MB limit.`;
    return null;
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    const error = validateFile(selected);
    if (error) { setStatus('error'); setMessage(error); setFile(null); return; }
    setFile(selected); setStatus(null); setMessage('');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files?.[0];
    if (!dropped) return;
    const error = validateFile(dropped);
    if (error) { setStatus('error'); setMessage(error); return; }
    setFile(dropped); setStatus(null); setMessage('');
  };

  const handleUpload = async () => {
    if (!file) return;
    setStatus('uploading'); setMessage('');
    try {
      await (config.noBot ? config.request(userId, file) : config.request(userId, bot, file));
      setStatus('success'); setMessage('File uploaded successfully.');
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (error) {
      setStatus('error');
      setMessage(error?.response?.data || error?.message || 'Upload failed. Please try again.');
    }
  };

  const handleClear = () => {
    setFile(null); setStatus(null); setMessage('');
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="hc-card">
      <div className="hc-card-header">
        <span className="hc-card-title">{config.title}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {config.downloadThunk && (
            <button
              className="hc-dropdown-trigger"
              onClick={() => dispatch(config.downloadThunk())}
              disabled={loading[config.loadingKey]}
              title="Download Template"
              style={{ gap: 6, color: '#16a34a', borderColor: '#bbf7d0' }}
            >
              {loading[config.loadingKey]
                ? <span className="hc-spinner" style={{ width: 12, height: 12 }} />
                : <ArticleIcon style={{ fontSize: 16, color: '#217346' }} />
              }
              Download Template
            </button>
          )}
          <span className="hc-badge" style={{ background: config.badge.bg, color: config.badge.color }}>
            {config.badge.label}
          </span>
        </div>
      </div>

      <div style={{ padding: '12px 16px 12px' }}>

        {/* Drop zone */}
        <div
          className={`hc-upload-zone${file ? ' hc-upload-zone--has-file' : ''}`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept={fileExt}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {file ? (
            <div className="hc-upload-file-info">
              <span className="hc-upload-file-icon">📄</span>
              <span className="hc-upload-file-name">{file.name}</span>
              <span className="hc-upload-file-size">({(file.size / 1024).toFixed(1)} KB)</span>
            </div>
          ) : (
            <div className="hc-upload-placeholder">
              <span className="hc-upload-icon">⬆</span>
              <span className="hc-upload-text">Drop your <strong>{fileExt}</strong> file here or <u>browse</u></span>
            </div>
          )}
        </div>

        {/* Feedback */}
        {status === 'success' && (
          <div className="hc-upload-feedback hc-upload-feedback--success">✔ {message}</div>
        )}
        {status === 'error' && (
          <div className="hc-upload-feedback hc-upload-feedback--error">✕ {message}</div>
        )}

        {/* Bot selector + actions on the same row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
          <div>
            {!config.noBot && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#64748b', fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>Bot</span>
                <select
                  value={bot}
                  onChange={(e) => setBot(Number(e.target.value))}
                  disabled={status === 'uploading' || config.lockedBot}
                  style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, fontFamily: "'Inter', sans-serif", color: '#334155', background: '#fff', cursor: 'pointer' }}
                >
                  {(config.botOptions ?? [1, 2, 3, 4, 5]).map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {file && (
              <button className="hc-btn hc-btn-ghost" onClick={handleClear} disabled={status === 'uploading'}>
                Clear
              </button>
            )}
            <button
              className="hc-btn hc-btn-primary"
              onClick={handleUpload}
              disabled={!file || status === 'uploading'}
            >
              {status === 'uploading'
                ? <><span className="hc-spinner" style={{ borderTopColor: '#fff', marginRight: 8 }} />Uploading…</>
                : 'Upload'
              }
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── OPERATIONS TOTALS ────────────────────────────────────────────────────────
function OperationsTotals({ data }) {
  const items = [
    { label: 'Creations', value: data?.creations ?? '—', color: '#1d4ed8', bg: '#eff6ff' },
    { label: 'Changes',   value: data?.changes   ?? '—', color: '#16a34a', bg: '#f0fdf4' },
    { label: 'Other',     value: data?.other     ?? '—', color: '#7c3aed', bg: '#faf5ff' },
  ];
  return (
    <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center', width: '100%' }}>
      <div style={{ padding: '0 0 6px', borderBottom: '1px solid #f1f5f9', marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>Totals</span>
      </div>
      {items.map((item) => (
        <div key={item.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, background: item.bg }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: '#475569', fontFamily: "'Inter', sans-serif" }}>{item.label}</span>
          <span style={{ fontSize: 18, fontWeight: 700, color: item.color, fontFamily: "'Inter', sans-serif" }}>{item.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── BOT SUMMARY TABLE ────────────────────────────────────────────────────────
function UsageTable({ rows = [], loading = false }) {
  const cellStyle = { padding: '5px 10px', fontSize: 11 };
  const headStyle = { padding: '5px 10px', fontSize: 10 };
  return (
    <>
      <div style={{ padding: '8px 14px 4px', borderBottom: '1px solid #f1f5f9' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>Usage</span>
      </div>
      <div className="hc-table-wrapper" style={{ height: 'auto', overflow: 'visible' }}>
        <table className="hc-table" style={{ fontSize: 11 }}>
          <thead>
            <tr>
              <th style={headStyle}>Bot</th>
              <th style={headStyle}>Creations</th>
              <th style={headStyle}>Changes</th>
              <th style={headStyle}>Other</th>
              <th style={headStyle}>Usage</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="hc-table-empty"><span className="hc-spinner" style={{ marginRight: 8 }} />Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="hc-table-empty">No data available</td></tr>
            ) : rows.map((row, i) => (
              <tr key={row.bot} style={{ background: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                <td style={cellStyle}>{row.bot}</td>
                <td style={cellStyle}>{row.creations}</td>
                <td style={cellStyle}>{row.changes}</td>
                <td style={cellStyle}>{row.other}</td>
                <td style={cellStyle}>{row.usage}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
function HelixUpload() {
  const classes         = useStyles();
  const dispatch        = useDispatch();
  const userId          = useSelector((state) => state.session.user?.id);
  const botSummary      = useSelector((state) => state.helixCrqsReducer.botSummary);
  const loading         = useSelector((state) => state.helixCrqsReducer.loading);

  const botSummaryRows = botSummary?.helixCrqsBotSummaryUsage       ?? [];
  const botSummaryOps  = botSummary?.helixCrqsBotSummaryOperations?.[0] ?? null;

  useEffect(() => {
    dispatch(hcGetBotSummary());
    const id = setInterval(() => dispatch(hcPollBotSummary()), 5000);
    return () => clearInterval(id);
  }, [dispatch]);

  return (
    <Page className={classes.root} title="Helix Upload">
      <Header module="NASP" view="Helix Upload" />
      <Card className={classes.container}>
        <CardContent style={{ padding: 0 }}>
          <div className="hc-root">
            <div style={{ padding: '32px 24px 16px' }}>
              <div className="hc-card" style={{ marginBottom: 0 }}>
                <div className="hc-card-header" style={{ padding: '10px 14px' }}>
                  <span className="hc-card-title">Bot Summary</span>
                </div>
                <div style={{ display: 'flex' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <UsageTable rows={botSummaryRows} loading={loading.botSummary} />
                  </div>
                  <div style={{ width: 220, flexShrink: 0, borderLeft: '1px solid #f1f5f9', display: 'flex', alignItems: 'stretch' }}>
                    <OperationsTotals data={botSummaryOps} />
                  </div>
                </div>
              </div>
            </div>

            <div style={{ padding: '0 24px 32px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
              {[...UPLOAD_CONFIGS, ...UPLOAD_LOCK_CONFIGS].map((config) => (
                <UploadCard key={config.key} config={config} userId={userId} dispatch={dispatch} loading={loading} />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </Page>
  );
}

export default HelixUpload;